import {
	Form,
	FormItem,
	ReorderableList
} from "client/Components";
import { AppDestinationWindowSelectFormItem } from "client/Features/Apps/AppDestinationWindowSelectFormItem";
import {
	AddTilesResponseModel,
	RecentUpdateDocumentsFolderModel,
	BookmarksUpdateDocumentsFolderModel,
	DocumentsUpdateTileModel,
	DocumentsAppDetailsTileModel,
	DocumentsAppTabDetailsModel,
	UpdateDocumentsAppTabModel,
	UpdateDocumentsFolderModel,
	updateDocumentsTile
} from "client/Api/Workspace/workspace";
import { IFormHandler, useFormHandler } from "client/Forms";
import { IValueInputAttachable } from "client/Forms/attachable";
import { createValidationConfiguration, IValidationConfiguration } from "client/Validation";
import {
	doesNotExceedLength,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useCallback,
	useImperativeHandle,
	useMemo,
	useState
} from "react";
import { never } from "client_legacy/ndaw/errors";
import { newGuid } from "client_legacy/ndaw/guid";
import { FolderIdentifier, FolderIdentifierKind } from "client/Api/Workspace/LiveInformation/Documents/documentsLiveInformation";
import { ExistingTile } from "../common";
import { EditTileFromData, initialGenericState } from "../EditGenericTile";
import { EditTileHandle } from "../EditTile";
import { EditTileGeneralFields } from "../GeneralFields";
import { useUpdateTile } from "../hooks/useUpdateTile";
import { EditDocumentsTabItem } from "./EditDocumentsTabItem";

const MAX_TAB_NUMBER = 20;

export const RECENT_FOLDER_REFERENCE: RecentUpdateDocumentsFolderModel = {
	recent: {
		// We need to send garbage else MVC model binding is nice enough to make the entire tree of objects
		// into null references
		_: 0
	}
};

export const BOOKMARKS_FOLDER_REFERENCE: BookmarksUpdateDocumentsFolderModel = {
	bookmarks: {
		// We need to send garbage else MVC model binding is nice enough to make the entire tree of objects
		// into null references
		_: 0
	}
};

type DocumentsEditFormData = EditTileFromData<DocumentsUpdateTileModel>;

const validationBuilder: IValidationConfiguration<DocumentsEditFormData> =
	createValidationConfiguration<DocumentsEditFormData>();

validationBuilder.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

const tabValidation = validationBuilder.when(
	(formData: DocumentsEditFormData): boolean =>	{
		const {columnSpan, rowSpan} = formData.dimension;
		return computeIsTabNeeded(columnSpan, rowSpan);
	}
).forCollection("tabs")
	.forItems(
		item => item.uniqueId
	);

tabValidation.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

tabValidation.forObject("folder")
	.add(
		isNotNullNorUndefined()
	);

export type EditDocumentsTileProps = {
	initialModel: DocumentsAppDetailsTileModel;
	mode: ExistingTile;
	onIsLoadingChange(isLoading: boolean): void;
	isLoading: boolean;
	onAfterSave: (response?: AddTilesResponseModel) => void;
};

export const EditDocumentsTile = forwardRef(
	(props: EditDocumentsTileProps, ref: Ref<EditTileHandle>): ReactElement => {
		const {
			initialModel,
			mode,
			onIsLoadingChange,
			isLoading,
			onAfterSave
		} = props;

		const [formData, setFormData] = useState<DocumentsEditFormData>({
			...initialGenericState(initialModel),
			destinationWindow: initialModel.destinationWindow,
			tabs: initialModel.tabs
				.map(
					(tab: DocumentsAppTabDetailsModel): UpdateDocumentsAppTabModel => convertToUpdateTabModel(tab)
				)
		});

		const formHandler: IFormHandler<DocumentsEditFormData> = useFormHandler(
			formData,
			setFormData,
			validationBuilder
		);

		const { columnSpan, rowSpan } = formHandler.for("dimension").value;
		const isTabsNeeded: boolean = useMemo(
			() => {
				return computeIsTabNeeded(columnSpan, rowSpan);
			},
			[columnSpan, rowSpan]
		);

		const { updateHandler: update } = useUpdateTile<DocumentsUpdateTileModel>(
			formHandler,
			updateDocumentsTile,
			isLoading,
			onIsLoadingChange,
			mode,
			onAfterSave,
			prepareModelForSaving
		);

		useImperativeHandle(
			ref,
			() => ({
				updateHandler: (): void => {
					update();
				}
			})
		);

		const itemMoveHandler = useCallback(
			(fromIndex: number, toIndex: number): void => {
				setFormData(
					(formState) => {
						const tabsCopy: UpdateDocumentsAppTabModel[] = [...formState.tabs];
						const item: UpdateDocumentsAppTabModel = tabsCopy.splice(fromIndex, 1)[0];
						tabsCopy.splice(toIndex, 0, item);

						return {
							...formState,
							tabs: tabsCopy
						};
					}
				);
			},
			[]
		);

		const addTabHandler = (): void => {
			const newTab: UpdateDocumentsAppTabModel = {
				name: "",
				uniqueId: newGuid(),
				folder: null
			};
			const tabsHandler: IValueInputAttachable<UpdateDocumentsAppTabModel[]> = formHandler.for("tabs");

			tabsHandler.reducer
				.push(newTab);

			tabsHandler.validation
				.triggerInputFinished();
		};

		const removeTabHandler = (index: number): void => {
			const tabsHandler: IValueInputAttachable<UpdateDocumentsAppTabModel[]> = formHandler.for("tabs");
			if (tabsHandler.value.length > 1) {
				tabsHandler.reducer
					.removeAt(index);

				tabsHandler.validation
					.triggerInputFinished();
			}
		};

		return (
			<Form onSubmit="NONE">
				<EditTileGeneralFields
					initialModel={initialModel}
					formHandler={formHandler}
				/>
				<AppDestinationWindowSelectFormItem
					attachable={formHandler.for("destinationWindow")}
				/>
				{isTabsNeeded && (
					<FormItem hasNoMargin>
						<ReorderableList onItemMove={itemMoveHandler}>
							{formHandler.for("tabs")
								.map((tabHandler, index) => (
									<EditDocumentsTabItem
										key={tabHandler.value.uniqueId}
										attachable={tabHandler}
										onRemoveTab={() => removeTabHandler(index)}
									/>
								))
							}
						</ReorderableList>
						{formData.tabs.length < MAX_TAB_NUMBER && (
							<ReorderableList.AddButton
								trackingId="add-tab-button"
								onClick={addTabHandler}
							/>
						)}
					</FormItem>
				)}
			</Form>
		);
	}
);

EditDocumentsTile.displayName = "EditDocumentsTile";

function computeIsTabNeeded(columnSpan: number, rowSpan: number): boolean{
	return columnSpan >= 4 && rowSpan >= 4;
}

function folderIdentifierToUpdateDocumentsFolderModel(folder: FolderIdentifier): UpdateDocumentsFolderModel{
	switch(folder.kind){
		case FolderIdentifierKind.Recent:
			return RECENT_FOLDER_REFERENCE;
		case FolderIdentifierKind.Bookmarks:
			return BOOKMARKS_FOLDER_REFERENCE;

		case FolderIdentifierKind.WebDavFolder:
			return {
				webDav: {
					serverId: folder.serverId,
					id: folder.id,
					name: folder.name
				}
			};

		case FolderIdentifierKind.SharePointFolder:
			return {
				sharePoint: {
					siteUrl: folder.siteUrl,
					listId: folder.listId,
					id: folder.id,
					name: folder.name
				}
			};

		default:
			never(folder);
	}
}

function convertToUpdateTabModel(tab: DocumentsAppTabDetailsModel): UpdateDocumentsAppTabModel{
	return {
		uniqueId: tab.uniqueId,
		name: tab.name,
		folder: folderIdentifierToUpdateDocumentsFolderModel(tab.folder)
	};
}

function prepareModelForSaving(formData: DocumentsEditFormData): DocumentsEditFormData{
	const {columnSpan, rowSpan} = formData.dimension;
	const isTabsNeeded = computeIsTabNeeded(columnSpan, rowSpan);

	return {
		...formData,
		tabs: isTabsNeeded ? formData.tabs : []
	};
}